import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import './Login.css';
import logoImage from '../img/citylink_logo.webp';
import { API_BASE } from '../config/apiConfig';

const Login = ({ variant = 'user' }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [quickLoginOptions, setQuickLoginOptions] = useState([]);
  const [showQuickLogin, setShowQuickLogin] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Load quick login options from localStorage
    const savedLogins = localStorage.getItem('savedLogins');
    if (savedLogins) {
      try {
        const logins = JSON.parse(savedLogins);
        setQuickLoginOptions(logins);
        if (logins.length > 0) {
          setShowQuickLogin(true);
        }
      } catch (e) {
        console.error('Failed to parse saved logins:', e);
      }
    }

    // If a valid session cookie exists, bootstrap from /auth/me
    const checkExistingSession = async () => {
      try {
        const response = await axios.get(`${API_BASE}/auth/me`, {
          withCredentials: true
        });
        if (response.data && response.data.success && response.data.user) {
          const user = response.data.user;
          localStorage.setItem('isAuthenticated', 'true');
          localStorage.setItem('userEmail', user.email);
          localStorage.setItem('userId', user.id);
          localStorage.setItem('loginTimestamp', new Date().toISOString());
          localStorage.setItem('currentUser', JSON.stringify(user));

          const selectedCounty = localStorage.getItem('selectedCounty');
          if (selectedCounty) {
            navigate(user.role === 'admin' ? '/admin' : '/dashboard');
          } else {
            navigate('/select');
          }
        }
      } catch {
        // no valid session cookie; stay on login
      }
    };

    checkExistingSession();
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      alert('Please enter both email and password');
      return;
    }

    try {
      // Get location if available
      const location = JSON.parse(localStorage.getItem('selectedCounty') || 'null');

      // Call login API with credentials to allow cookies
      const response = await axios.post(`${API_BASE}/auth/login`, {
        email,
        password,
        rememberMe,
        location
      }, {
        withCredentials: true // Important: allows cookies to be sent/received
      });

      if (response.data.success) {
        // Store user info in localStorage (not auth token - that's in cookie)
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('userEmail', response.data.user.email);
        localStorage.setItem('userId', response.data.user.id);
        localStorage.setItem('loginTimestamp', new Date().toISOString());

        // Save login info if remember me is checked
        if (rememberMe) {
          const loginInfo = {
            email: response.data.user.email,
            timestamp: new Date().toISOString(),
            location: response.data.user.city ? {
              state: response.data.user.state,
              stateAbbr: response.data.user.state?.substring(0, 2) || '',
              city: response.data.user.city,
              county: response.data.user.county
            } : location
          };

          // Get existing saved logins
          const savedLogins = JSON.parse(localStorage.getItem('savedLogins') || '[]');
          
          // Remove existing login with same email
          const filteredLogins = savedLogins.filter(login => login.email !== email);
          
          // Add new login at the beginning
          const updatedLogins = [loginInfo, ...filteredLogins].slice(0, 5); // Keep max 5
          
          localStorage.setItem('savedLogins', JSON.stringify(updatedLogins));
        }

        // DO NOT auto-store location from server - user must select on county selector page
        // Clear any existing selected county to force selection
        localStorage.removeItem('selectedCounty');
        
        // Store user object for location selector
        localStorage.setItem('currentUser', JSON.stringify(response.data.user));

        // Always navigate to location selector after login
        // Store user info and saved locations for location selector
        const savedLogins = JSON.parse(localStorage.getItem('savedLogins') || '[]');
        const userSavedLocations = savedLogins
          .filter(login => login.email === response.data.user.email)
          .map(login => login.location)
          .filter(loc => loc && loc.city); // Only include valid locations

        localStorage.setItem('currentUser', JSON.stringify(response.data.user));
        localStorage.setItem('userSavedLocations', JSON.stringify(userSavedLocations));
        
        // ALWAYS navigate to select page after login (never auto-use location)
        navigate('/select');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Login failed';
      alert(errorMessage);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      alert('Please enter both email and password');
      return;
    }

    if (password.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }

    try {
      const response = await axios.post(`${API_BASE}/auth/register`, {
        email,
        password,
        fullName: fullName || undefined
      });

      if (response.data.success) {
        alert('Registration successful! Please login with your credentials.');
        setIsRegistering(false);
        setFullName('');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Registration failed';
      alert(errorMessage);
    }
  };

  const handleQuickLogin = async (loginInfo) => {
    try {
      // Check if there's a valid session cookie by calling /api/auth/me
      try {
        const response = await axios.get(`${API_BASE}/auth/me`, {
          withCredentials: true
        });
        
        if (response.data.success) {
          // User is already authenticated, proceed with location selection
          localStorage.setItem('isAuthenticated', 'true');
          localStorage.setItem('userEmail', response.data.user.email);
          localStorage.setItem('userId', response.data.user.id);
          localStorage.setItem('loginTimestamp', new Date().toISOString());
          localStorage.setItem('currentUser', JSON.stringify(response.data.user));
          
          // Navigate to location selector
          navigate('/select');
          return;
        }
      } catch (error) {
        // No valid session, need to login
      }
      
      // If no valid session, pre-fill email and show login form
      setEmail(loginInfo.email);
      setShowQuickLogin(false);
      // Focus on password field
      setTimeout(() => {
        const passwordInput = document.getElementById('password');
        if (passwordInput) passwordInput.focus();
      }, 100);
    } catch (error) {
      alert('Quick login failed. Please use regular login.');
    }
  };

  const handleRemoveQuickLogin = (email, e) => {
    e.stopPropagation();
    const savedLogins = JSON.parse(localStorage.getItem('savedLogins') || '[]');
    const filteredLogins = savedLogins.filter(login => login.email !== email);
    localStorage.setItem('savedLogins', JSON.stringify(filteredLogins));
    setQuickLoginOptions(filteredLogins);
    if (filteredLogins.length === 0) {
      setShowQuickLogin(false);
    }
  };

  const handleNewLogin = () => {
    setShowQuickLogin(false);
    setEmail('');
    setPassword('');
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <span className="login-logo-mark">
              <img src={logoImage} alt="CityLink logo" className="login-logo-img" />
            </span>
            <span className="login-logo-text">
              <span className="login-logo-city">CITY</span>
              <span className="login-logo-link">LINK</span>
            </span>
          </div>
          <h1>{variant === 'staff' ? 'City staff sign in' : 'Sign In'}</h1>
        </div>

        {showQuickLogin && quickLoginOptions.length > 0 ? (
          <div className="quick-login-section">
            <div className="quick-login-header">
              <h2>Quick Login</h2>
              <button className="new-login-button" onClick={handleNewLogin}>
                Use different account
              </button>
            </div>
            <div className="quick-login-list">
              {quickLoginOptions.map((login, index) => (
                <div
                  key={index}
                  className="quick-login-card"
                  onClick={() => handleQuickLogin(login)}
                >
                  <div className="quick-login-info">
                    <div className="quick-login-email">{login.email}</div>
                    {login.location && login.location.city && (
                      <div className="quick-login-location">
                        {login.location.city}, {login.location.county}
                      </div>
                    )}
                    <div className="quick-login-time">
                      Last login: {new Date(login.timestamp).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="quick-login-actions">
                    <button
                      className="remove-login-button"
                      onClick={(e) => handleRemoveQuickLogin(login.email, e)}
                      title="Remove from quick login"
                    >
                      ×
                    </button>
                    <div className="quick-login-arrow">→</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <form className="login-form" onSubmit={isRegistering ? handleRegister : handleLogin}>
            {isRegistering && (
              <div className="form-group">
                <label htmlFor="fullName">Full Name (Optional)</label>
                <input
                  id="fullName"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="John Doe"
                  autoComplete="name"
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isRegistering ? "At least 6 characters" : "Enter your password"}
                required
                autoComplete={isRegistering ? "new-password" : "current-password"}
                minLength={isRegistering ? 6 : undefined}
              />
            </div>

            {!isRegistering && (
              <div className="form-options">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <span>Remember me for quick login</span>
                </label>
              </div>
            )}

            <button type="submit" className="login-button">
              {isRegistering ? 'Create Account' : 'Sign In'}
            </button>

            <div className="form-footer">
              <p className="form-footer-admin-link">
                <Link to="/admin/register">Government staff? Create admin account</Link>
              </p>
              {isRegistering ? (
                <button
                  type="button"
                  className="form-link"
                  onClick={() => {
                    setIsRegistering(false);
                    setFullName('');
                  }}
                >
                  Already have an account? Sign in
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="form-link"
                    onClick={() => setIsRegistering(true)}
                  >
                    Don't have an account? Register
                  </button>
                  {quickLoginOptions.length > 0 && (
                    <button
                      type="button"
                      className="quick-login-toggle"
                      onClick={() => setShowQuickLogin(true)}
                    >
                      Use quick login instead
                    </button>
                  )}
                </>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default Login;
